import firebase, { DocumentSnapshot } from "store/firebase";
import "firebase/firestore";

import { AnyAction, Dispatch } from "redux";
import { User } from "./reducer";
import { userFromDoc } from "./utils";
import { createSubscriptionRegistry } from "store/util/subscription-registry";

export const USER_MODIFIED_EVENT = "user-modified";
export const ACTION_USERS_LISTEN_CHANGE = "users/listen_change";

/**
 * Per-email Firestore subscription registry. Each unique user email gets one
 * listener; resubscribing for the same email is a no-op (the existing
 * listener stays). When `subscribe(emails)` is called with a new set, listeners
 * not in the new set are released so we don't accumulate dead subscriptions.
 *
 * Replaces the singleton `let _unsubscribe` that cancelled every listener
 * on every navigation, even between events that share users.
 */
const subscriptions = createSubscriptionRegistry();

export function subscribe(emails: (string | null)[]) {
  const valid = new Set(
    emails.filter((email): email is string => Boolean(email))
  );
  // Drop listeners for emails no longer in scope.
  for (const id of Array.from(emails)) {
    if (id && !valid.has(id)) {
      subscriptions.unsubscribe(id);
    }
  }
  // Register listeners for any emails not already being watched.
  for (const email of Array.from(valid)) {
    if (subscriptions.isSubscribed(email)) continue;
    subscriptions.subscribe(email, () => {
      const ref = firebase.firestore().collection("users").doc(email);
      return ref.onSnapshot((snapshot: DocumentSnapshot) => {
        const userData = userFromDoc(snapshot);
        const windowEvent = new CustomEvent(USER_MODIFIED_EVENT, {
          detail: userData,
        });
        window.dispatchEvent(windowEvent);
      }, console.log);
    });
  }
}

export function unsubscribe(email?: string) {
  if (email) {
    subscriptions.unsubscribe(email);
  } else {
    subscriptions.unsubscribeAll();
  }
}

export default function listenChange(event: User) {
  return async (dispatch: Dispatch<AnyAction>) => {
    dispatch({ type: ACTION_USERS_LISTEN_CHANGE, payload: event });
  };
}
